/**
 * FPS Tracker
 * Tracks the page's frames per second using requestAnimationFrame.
 */

import { isTabFocused, getVisibilityChangeEventName } from '@ezugi/hooks/useAppVisibility';

class FPSTracker {

  private static instance: FPSTracker | null = null;

  private lastTime = 0;

  private currentFPS = 0;

  private frameCount = 0;

  private rafId: number | null = null;

  private static readonly supported = typeof window !== 'undefined'
    && typeof window.requestAnimationFrame === 'function'
    && typeof window.performance !== 'undefined'
    && typeof window.performance.now === 'function';

  // eslint-disable-next-line no-useless-constructor, no-empty-function
  private constructor(private readonly sampleInterval = 1000) {}

  static get isSupported() {
    return this.supported;
  }

  static getInstance(): FPSTracker {
    if (!this.instance) {
      this.instance = new FPSTracker();
      this.instance.start();
    }
    return this.instance;
  }

  start() {
    if (this.rafId !== null) return;

    this.lastTime = performance.now();
    this.frameCount = 0;
    this.rafId = requestAnimationFrame(this.tick);

    if (typeof document !== 'undefined') {
      document.addEventListener(getVisibilityChangeEventName(), this.handleVisibilityChange);
    }
  }

  getFPS() {
    return Math.round(this.currentFPS);
  }

  private handleVisibilityChange = () => {
    if (!isTabFocused()) {
      // stop tracking
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      return;
    }

    // resume tracking
    if (this.rafId === null) {
      this.lastTime = performance.now();
      this.frameCount = 0;
      this.currentFPS = 0;
      this.rafId = requestAnimationFrame(this.tick);
    }
  };

  private tick = () => {
    const now = performance.now();
    this.frameCount++;

    const elapsed = now - this.lastTime;

    if (elapsed >= this.sampleInterval) {
      this.currentFPS = (this.frameCount * 1000) / elapsed;
      this.frameCount = 0;
      this.lastTime = now;
    }

    this.rafId = requestAnimationFrame(this.tick);
  };

}

export const getCurrentFPS = () => {
  if (!FPSTracker.isSupported) return 0;
  return FPSTracker.getInstance().getFPS();
};
